import { useHandler } from "@redotech/react-util/hook";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import * as gridCss from "@redotech/redo-web/grid.module.css";
import { Text } from "@redotech/redo-web/text";
import { groupInput, input, InputSchema, listInput } from "@redotech/ui/form";
import { numberValidator } from "@redotech/ui/validators";
import { memo, useEffect, useRef, useState } from "react";
import * as styles from "./forecasting-cost-breakdown.module.css";

// Form schema for a single bucket
const bucketForm = groupInput({
  upperBound: input<string>({ validator: numberValidator({ min: 1 }) }),
  price: input<string>({ validator: numberValidator({ min: 0 }) }),
  currency: input<string>(),
  skuCount: input<number>(),
});

// Default bucket factory
const bucketDefault = (
  lastUpperBound?: number,
): InputSchema.Value<typeof bucketForm> => {
  const upperBound = lastUpperBound ? lastUpperBound * 2 : 10;
  return {
    upperBound: upperBound.toString(),
    price: "0",
    currency: "USD",
    skuCount: 0,
  };
};

// Form for the entire list of buckets
export const forecastingCostBreakdownForm = groupInput({
  buckets: listInput(
    () => bucketForm,
    () => bucketDefault(),
    (_, index) => index,
  ),
  catchAllSkuCount: input<number>(),
  catchAllPrice: input<string>({ validator: numberValidator({ min: 0 }) }),
  catchAllCurrency: input<string>(),
});

export type ForecastingCostBreakdownForm = InputSchema.Input<
  typeof forecastingCostBreakdownForm
>;

export type ForecastingCostBreakdownValue = InputSchema.Value<
  typeof forecastingCostBreakdownForm
>;

export const ForecastingCostBreakdownCard = memo(
  function ForecastingCostBreakdownCard({
    input,
  }: {
    input: ForecastingCostBreakdownForm;
  }) {
    const { buckets, catchAllSkuCount, catchAllPrice } = input.inputs;
    const [highlightTotals, setHighlightTotals] = useState(false);
    const prevBucketsLengthRef = useRef(buckets.value.length);
    const prevUpperBoundsRef = useRef(
      buckets.value.map((b) => b.upperBound).join(","),
    );

    // Track changes to buckets count and upperBound values
    useEffect(() => {
      const currentUpperBounds = buckets.value
        .map((b) => b.upperBound)
        .join(",");

      // Check if rows were added/deleted or upperBound changed
      if (
        buckets.value.length !== prevBucketsLengthRef.current ||
        currentUpperBounds !== prevUpperBoundsRef.current
      ) {
        setHighlightTotals(true);
        // Remove highlight after animation
        const timer = setTimeout(() => setHighlightTotals(false), 2000);

        // Update refs
        prevBucketsLengthRef.current = buckets.value.length;
        prevUpperBoundsRef.current = currentUpperBounds;

        return () => clearTimeout(timer);
      }

      return undefined;
    }, [buckets.value]);

    // Add new bucket (2x last bucket's upperBound)
    const handleAddBucket = useHandler(() => {
      // Filter out catch-all bucket (upperBound === -1) and find the biggest upperBound
      const validBuckets = buckets.value.filter(
        (b) => Number(b.upperBound) > 0,
      );
      const maxUpperBound =
        validBuckets.length > 0
          ? Math.max(...validBuckets.map((b) => Number(b.upperBound)))
          : undefined;

      input.setValue({
        ...input.value,
        buckets: [...buckets.value, bucketDefault(maxUpperBound)],
      });
    });

    // Delete bucket at index
    const handleDeleteBucket = useHandler((index: number) => {
      input.setValue({
        ...input.value,
        buckets: buckets.value.filter((_, i) => i !== index),
      });
    });

    // Update a specific field in a bucket
    const handleUpdateBucket = useHandler(
      (index: number, field: "upperBound" | "price", value: string) => {
        const updatedBuckets = [...buckets.value];
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        updatedBuckets[index] = { ...updatedBuckets[index], [field]: value };
        input.setValue({ ...input.value, buckets: updatedBuckets });
      },
    );

    return (
      <Card title="Forecasting cost breakdown">
        <section className={gridCss.grid}>
          <div className={gridCss.span12}>
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Orders/Mo</th>
                    <th># of SKUs</th>
                    <th>Price per SKU</th>
                    <th>Total/SKU</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {buckets.inputs
                    .map((bucketInput, index) => ({
                      input: bucketInput,
                      index,
                      upperBound: Number(bucketInput.inputs.upperBound.value),
                    }))
                    .sort((a, b) => a.upperBound - b.upperBound)
                    .map(({ input: bucketInput, index: originalIndex }) => {
                      const { upperBound, price, skuCount } =
                        bucketInput.inputs;
                      const numPrice = Number(price.value) || 0;
                      const numSkuCount = Number(skuCount.value) || 0;
                      const total = numPrice * numSkuCount;

                      return (
                        <tr key={originalIndex}>
                          <td>
                            <RedoTextInput
                              placeholder="e.g., 10"
                              setValue={(value: string) =>
                                handleUpdateBucket(
                                  originalIndex,
                                  "upperBound",
                                  value,
                                )
                              }
                              size="sm"
                              value={upperBound.value}
                            />
                          </td>
                          <td>
                            <Text fontSize="sm">{numSkuCount}</Text>
                          </td>
                          <td>
                            <Flex align="center" gap="xs">
                              <Text fontSize="sm">$</Text>
                              <RedoTextInput
                                placeholder="e.g., 5.00"
                                setValue={(value: string) =>
                                  handleUpdateBucket(
                                    originalIndex,
                                    "price",
                                    value,
                                  )
                                }
                                size="sm"
                                value={price.value}
                              />
                            </Flex>
                          </td>
                          <td>
                            <Text fontSize="sm">${total.toFixed(2)}</Text>
                          </td>
                          <td>
                            {buckets.inputs.length > 1 && (
                              <RedoButton
                                onClick={() =>
                                  handleDeleteBucket(originalIndex)
                                }
                                size="sm"
                                text="Delete"
                                theme="normal"
                              />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  {/* Catch-all row for SKUs above the highest bucket */}
                  {buckets.inputs.length > 0 &&
                    (() => {
                      const catchAllPriceNum = Number(catchAllPrice.value) || 0;
                      const catchAllSkuCountNum =
                        Number(catchAllSkuCount.value) || 0;
                      const catchAllTotal =
                        catchAllPriceNum * catchAllSkuCountNum;

                      return (
                        <tr>
                          <td>
                            <Text fontSize="sm" fontWeight="medium">
                              Above{" "}
                              {Math.max(
                                ...buckets.value
                                  .filter((b) => Number(b.upperBound) > 0)
                                  .map((b) => Number(b.upperBound)),
                              )}
                            </Text>
                          </td>
                          <td>
                            <Text fontSize="sm">{catchAllSkuCountNum}</Text>
                          </td>
                          <td>
                            <Flex align="center" gap="xs">
                              <Text fontSize="sm">$</Text>
                              <RedoTextInput
                                placeholder="e.g., 5.00"
                                setValue={(value: string) =>
                                  catchAllPrice.setValue(value)
                                }
                                size="sm"
                                value={catchAllPrice.value}
                              />
                            </Flex>
                          </td>
                          <td>
                            <Text fontSize="sm">
                              ${catchAllTotal.toFixed(2)}
                            </Text>
                          </td>
                          <td />
                        </tr>
                      );
                    })()}

                  {/* Totals row */}
                  {(() => {
                    // Calculate total SKU count
                    let totalSkuCount = 0;
                    let totalPrice = 0;

                    // Add SKU counts and totals from all buckets
                    for (const bucketInput of buckets.inputs) {
                      const skuCount =
                        Number(bucketInput.inputs.skuCount.value) || 0;
                      const price = Number(bucketInput.inputs.price.value) || 0;
                      totalSkuCount += skuCount;
                      totalPrice += skuCount * price;
                    }

                    // Add catch-all row values
                    const catchAllSkuCountNum =
                      Number(catchAllSkuCount.value) || 0;
                    const catchAllPriceNum = Number(catchAllPrice.value) || 0;
                    totalSkuCount += catchAllSkuCountNum;
                    totalPrice += catchAllSkuCountNum * catchAllPriceNum;

                    return (
                      <tr
                        className={`${styles.totalsRow} ${highlightTotals ? styles.highlighted : ""}`}
                      >
                        <td>
                          <Text fontSize="sm" fontWeight="semibold">
                            TOTAL
                          </Text>
                        </td>
                        <td>
                          <Text fontSize="sm" fontWeight="semibold">
                            {totalSkuCount}
                          </Text>
                        </td>
                        <td>{/* Empty cell for Price column */}</td>
                        <td>
                          <Text fontSize="sm" fontWeight="semibold">
                            ${totalPrice.toFixed(2)}
                          </Text>
                        </td>
                        <td />
                      </tr>
                    );
                  })()}
                </tbody>
              </table>
            </div>

            <Flex gap="md" style={{ marginTop: "var(--redo-spacing-xl)" }}>
              <RedoButton
                onClick={handleAddBucket}
                size="sm"
                text="Add row"
                theme="normal"
              />
            </Flex>
          </div>
        </section>
      </Card>
    );
  },
);
